import clsx from 'clsx';
import { useState } from 'react';

import { Message } from '@/services/types/artifact-types';

import { RecipientsList } from './RecipientsList';
import { SenderInfo } from './SenderInfo';
import { getRecipientsByRole, getSender } from './utils';

interface ConversationParticipantsBlockProps {
  message: Message;
  showRecipients?: boolean;
}

export const ConversationParticipantsBlock = ({
  message,
  showRecipients = true,
}: ConversationParticipantsBlockProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const sender = getSender(message);
  const recipientsByRole = getRecipientsByRole(message);
  const allRecipients = Object.values(recipientsByRole).flat();
  const hasMultipleRecipients = allRecipients.length > 1;

  const handleToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    // Only toggle if no text is selected
    if (hasMultipleRecipients && !window.getSelection()?.toString()) {
      setIsExpanded((prev) => !prev);
    }
  };

  if (!showRecipients) {
    return <SenderInfo sender={sender} />;
  }

  return (
    <button
      type="button"
      onClick={handleToggle}
      className={clsx(
        'flex flex-col gap-1 min-w-0 flex-1 text-left transition-colors',
        hasMultipleRecipients &&
          'hover:bg-gray-100 rounded-md px-1 py-0.5 -mx-1 -my-0.5',
        !hasMultipleRecipients && 'cursor-default',
      )}
      disabled={!hasMultipleRecipients}
      aria-label={
        hasMultipleRecipients
          ? isExpanded
            ? 'Hide recipients'
            : 'Show all recipients'
          : undefined
      }
    >
      <SenderInfo sender={sender} />
      <RecipientsList message={message} isExpanded={isExpanded} />
      {message.subject && (
        <div className="text-xs text-gray-600 truncate mt-0.5">
          <span className="font-medium text-gray-500 mr-1">Subject:</span>
          {message.subject}
        </div>
      )}
    </button>
  );
};
